import { Controller, useFormContext } from "react-hook-form";
import FileDropZone, { FileInputParams } from "../../file_upload/FileDropZone";
import { InputBaseParams } from "../Form";
import useImageCrop, { CropOptions } from "../../file_upload/useImageCrop";
import { ImageSize } from "../../file_upload/ImageCrop";

/**
 * A file input that allows users
 * to upload single/multiple files.
 *
 * Also provides a change handler useful for
 * listing files while still using form
 * error system.
 */
export const FileInput = ({
  name,
  value,
  accept,
  multiple,
  maxSize,
  cropImage,
  className,
  error,
  handleChange,
  onChange,
  setError
}: FileInputParams & {
  name?: string;
  value: File[] | undefined;
  cropImage?: CropOptions;
  error?: boolean;
  className?: string;
  handleChange?: () => void;
  onChange: (value: Blob[]) => void;
  setError?: (message: string) => void;
}) => {
  const { startCrop, cropElement } = useImageCrop(
    cropImage?.title,
    cropImage?.cropSize,
    cropImage?.previewSize,
    cropImage?.minZoom,
    cropImage?.maxZoom
  );

  const runMiddleware = (value: File[]) => {
    if (!cropImage) {
      onChange(value);
      if (handleChange) handleChange();
    } else {
      startCrop(value[0], image => {
        onChange([image]);
        if (handleChange) handleChange();
      });
    }
  };

  return (
    <>
      <FileDropZone
        name={name}
        value={value}
        onChange={value => {
          // Use middleware to intercept images to crop
          runMiddleware(value);
        }}
        accept={accept}
        multiple={multiple}
        maxSize={maxSize}
        error={error}
        setError={setError}
        className={className}
      />

      {cropElement}
    </>
  );
};

const ControlledFileInput = (
  props: InputBaseParams &
    FileInputParams & {
      cropImage?: {
        title: string;
        cropSize: ImageSize;
        previewSize: ImageSize;
        minZoom?: number;
        maxZoom?: number;
      };
      handleChange?: () => void;
    }
) => {
  const { control, setError, clearErrors } = useFormContext();

  return (
    <Controller
      name={props.name}
      rules={props.rules}
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FileInput
          {...props}
          value={value}
          error={error !== undefined}
          onChange={value => {
            clearErrors(props.name);
            onChange(value);
          }}
          setError={message => setError(`root.${props.name}`, { message })}
        />
      )}
    />
  );
};

export default ControlledFileInput;
